import { useEffect, useMemo, useState } from "react";
import { roi, suppliers as suppliersApi } from "@/api";
import { useAuth } from "@/auth";
import { PILLARS } from "@/constants";
import { TrendingUp, Plus, CheckCircle2, Trash2, Target, Coins } from "lucide-react";

const PILLAR_LABELS = Object.fromEntries(PILLARS.map((p) => [p.key, p.label]));

const PILLAR_COLORS = {
  calidad: "#E4002B", costo: "#D4A437", continuidad: "#0033AD",
  innovacion: "#7B3FBF", sustentabilidad: "#009639", relacion: "#0077B6",
};
const mxn = (v) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(v || 0);

export default function ImpactoEconomico() {
  const { user } = useAuth();
  const canCapture = ["admin", "owner", "buyer", "evaluator"].includes(user?.role);
  const isAdmin = ["admin", "owner"].includes(user?.role);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [supps, setSupps] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", pillar: "costo", title: "", description: "", amount_mxn: "", period: "2026" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = () => {
    roi.summary().then(setSummary).catch(() => {});
    roi.list().then((r) => setProjects(r.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (canCapture) suppliersApi.list().then((r) => setSupps(r.items || r)).catch(() => {});
  }, [canCapture]);

  const maxPillar = useMemo(() => Math.max(1, ...(summary?.by_pillar || []).map((p) => p.validado + p.estimado)), [summary]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    try {
      await roi.create({ ...form, amount_mxn: Number(form.amount_mxn) });
      setMsg(isAdmin ? "Proyecto registrado y validado." : "Proyecto registrado como estimado — el admin lo validará.");
      setShowForm(false);
      setForm({ supplier_id: "", pillar: "costo", title: "", description: "", amount_mxn: "", period: "2026" });
      load();
    } catch (e2) { setErr(e2.response?.data?.detail || e2.message); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="roi-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2"><TrendingUp size={18} /> palancas de valor</div>
          <h1 className="font-display text-4xl">Impacto económico del programa</h1>
          <p className="text-[var(--ink-soft)] mt-1 max-w-3xl">
            Valor generado con proveedores por pilar: economías, costos evitados e ingresos por innovación.
            Meta oficial: <b>1–2% de economías por categoría</b>.
          </p>
        </div>
        {canCapture && (
          <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm" data-testid="roi-new-btn">
            <Plus size={15} /> Registrar proyecto de valor
          </button>
        )}
      </div>

      {msg && <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="roi-msg">{msg}</div>}
      {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="roi-err">{err}</div>}

      {showForm && (
        <form onSubmit={submit} className="sketch-border bg-[var(--cream-soft)] p-5 mb-6 grid md:grid-cols-2 gap-3" data-testid="roi-form">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Proveedor</label>
            <select required value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="input-field mt-1" data-testid="roi-form-supplier">
              <option value="">Selecciona…</option>
              {supps.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.category}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Pilar (palanca de valor)</label>
            <select value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value })} className="input-field mt-1" data-testid="roi-form-pillar">
              {Object.entries(PILLAR_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Nombre del proyecto</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej. Optimización de resina en preformas" className="input-field mt-1" data-testid="roi-form-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Monto (MXN)</label>
              <input required type="number" min="1" step="0.01" value={form.amount_mxn} onChange={(e) => setForm({ ...form, amount_mxn: e.target.value })} className="input-field mt-1" data-testid="roi-form-amount" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Período</label>
              <input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="2026" className="input-field mt-1" data-testid="roi-form-period" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Descripción (opcional)</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Qué se hizo y cómo se calculó el valor" className="input-field mt-1" data-testid="roi-form-desc" />
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="btn-primary text-sm" data-testid="roi-form-submit">Guardar proyecto</button>
          </div>
        </form>
      )}

      {/* Resumen */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="sketch-border bg-white p-5" data-testid="roi-total-validado">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--lala-blue)] flex items-center gap-1"><CheckCircle2 size={12} /> Valor validado</div>
          <div className="font-display text-3xl mt-1 text-[var(--lala-green,#009639)]" style={{ color: "#009639" }}>{mxn(summary?.total_validado)}</div>
        </div>
        <div className="sketch-border bg-white p-5" data-testid="roi-total-estimado">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--lala-blue)] flex items-center gap-1"><Coins size={12} /> Valor estimado (por validar)</div>
          <div className="font-display text-3xl mt-1 text-amber-600">{mxn(summary?.total_estimado)}</div>
        </div>
        <div className="sketch-border bg-white p-5" data-testid="roi-total-proyectos">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--lala-blue)] flex items-center gap-1"><Target size={12} /> Proyectos de valor</div>
          <div className="font-display text-3xl mt-1">{summary?.total_projects ?? 0}</div>
        </div>
      </div>

      {/* Por pilar */}
      <div className="sketch-border bg-[var(--cream-soft)] p-5 mb-6" data-testid="roi-by-pillar">
        <div className="font-display text-xl mb-3">Palancas de valor por pilar</div>
        <div className="space-y-3">
          {(summary?.by_pillar || []).map((p) => (
            <div key={p.pillar} className="grid md:grid-cols-[220px,1fr,140px] gap-2 items-center" data-testid={`roi-pillar-${p.pillar}`}>
              <div>
                <div className="font-semibold text-sm">{p.pillar_label}</div>
                <div className="text-[10px] text-[var(--ink-soft)]">{p.lever}</div>
              </div>
              <div className="h-5 bg-white rounded-full overflow-hidden border border-[var(--line)] flex">
                <div style={{ width: `${(p.validado / maxPillar) * 100}%`, background: PILLAR_COLORS[p.pillar] }} title="Validado" />
                <div style={{ width: `${(p.estimado / maxPillar) * 100}%`, background: PILLAR_COLORS[p.pillar], opacity: 0.35 }} title="Estimado" />
              </div>
              <div className="text-right text-sm font-bold">{mxn(p.validado + p.estimado)}
                <div className="text-[10px] font-normal text-[var(--ink-soft)]">{p.projects} proyecto{p.projects === 1 ? "" : "s"}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Por categoría vs meta */}
      <div className="sketch-border bg-white p-5 mb-6" data-testid="roi-by-category">
        <div className="font-display text-xl mb-1">Economías por categoría vs meta 1–2%</div>
        <p className="text-xs text-[var(--ink-soft)] mb-3">% = valor validado / gasto anual de la categoría.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ink-soft)] border-b border-[var(--line)]">
                <th className="py-2">Categoría</th><th>Validado</th><th>Estimado</th><th>Gasto anual</th><th>% economías</th><th>Meta</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.by_category || []).filter((c) => c.projects > 0 || c.savings_pct != null).map((c) => (
                <tr key={c.category} className="border-b border-[var(--line)]/60" data-testid={`roi-cat-${c.category}`}>
                  <td className="py-2 font-semibold">{c.category}</td>
                  <td>{mxn(c.validado)}</td>
                  <td className="text-[var(--ink-soft)]">{mxn(c.estimado)}</td>
                  <td>{c.annual_spend_mxn ? mxn(c.annual_spend_mxn) : "—"}</td>
                  <td className="font-bold">{c.savings_pct != null ? `${c.savings_pct}%` : "—"}</td>
                  <td>
                    <span className="text-[10px] text-[var(--ink-soft)] mr-1">{c.target_min_pct ?? 1}–{c.target_max_pct ?? 2}%</span>
                    {c.savings_pct == null ? <span className="text-[var(--ink-soft)] text-xs">sin gasto</span>
                      : c.savings_pct >= (c.target_min_pct ?? 1) ? <span className="text-emerald-700 font-bold text-xs">✓ en meta</span>
                      : <span className="text-amber-700 font-bold text-xs">{((c.target_min_pct ?? 1) - c.savings_pct).toFixed(2)}% para la meta</span>}
                  </td>
                </tr>
              ))}
              {(summary?.by_category || []).filter((c) => c.projects > 0 || c.savings_pct != null).length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-[var(--ink-soft)]">Aún no hay proyectos registrados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Proyectos */}
      <div className="sketch-border bg-[var(--cream-soft)] p-5" data-testid="roi-projects">
        <div className="font-display text-xl mb-3">Proyectos de valor ({projects.length})</div>
        <div className="space-y-2">
          {projects.map((p) => (
            <div key={p.id} className="bg-white rounded-lg border border-[var(--line)] p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`roi-project-${p.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: PILLAR_COLORS[p.pillar] }} />
                  <span className="font-semibold">{p.title}</span>
                  <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${p.status === "validado" ? "bg-emerald-50 text-emerald-700 border border-emerald-300" : "bg-amber-50 text-amber-700 border border-amber-300"}`} data-testid={`roi-status-${p.id}`}>
                    {p.status}
                  </span>
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-0.5">
                  {p.supplier_name} · {PILLAR_LABELS[p.pillar] || p.pillar} · {p.period} · por {p.created_by}
                </div>
                {p.description && <div className="text-xs text-[var(--ink-soft)] mt-0.5 italic">{p.description}</div>}
              </div>
              <div className="flex items-center gap-3">
                <div className="font-display text-xl">{mxn(p.amount_mxn)}</div>
                {isAdmin && p.status !== "validado" && (
                  <button onClick={() => roi.validate(p.id).then(load)} className="sketch-pill bg-emerald-600 text-white px-3 py-1 text-xs font-bold" data-testid={`roi-validate-${p.id}`}>
                    <CheckCircle2 size={12} className="inline mr-1" />Validar
                  </button>
                )}
                {isAdmin && (
                  <button onClick={() => { if (window.confirm("¿Eliminar este proyecto?")) roi.remove(p.id).then(load); }} className="text-red-600 hover:text-red-800" data-testid={`roi-delete-${p.id}`}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {projects.length === 0 && <div className="text-center text-[var(--ink-soft)] py-8">Sin proyectos todavía — registra el primero.</div>}
        </div>
      </div>
    </div>
  );
}
